import React from 'react';
import { useTranslation } from 'react-i18next';

const FloorInfo = () => {
  const { t } = useTranslation();

  return (
    <section id="floor-info" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-wood-dark mb-4">
            {t('floorInfo.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('floorInfo.subtitle')}
          </p>
        </div>

        {/* Oak Width Options */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-wood-dark mb-8 text-center">
            {t('floorInfo.oakWidth.title')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <div className="text-4xl font-bold text-wood-brown mb-2">{t('floorInfo.oakWidth.strip.size')}</div>
              <h4 className="font-semibold mb-2">{t('floorInfo.oakWidth.strip.name')}</h4>
              <p className="text-gray-600 text-sm">{t('floorInfo.oakWidth.strip.description')}</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <div className="text-4xl font-bold text-wood-brown mb-2">{t('floorInfo.oakWidth.standard.size')}</div>
              <h4 className="font-semibold mb-2">{t('floorInfo.oakWidth.standard.name')}</h4>
              <p className="text-gray-600 text-sm">{t('floorInfo.oakWidth.standard.description')}</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg text-center border-2 border-wood-brown">
              <div className="text-4xl font-bold text-wood-brown mb-2">{t('floorInfo.oakWidth.wide.size')}</div>
              <h4 className="font-semibold mb-2">{t('floorInfo.oakWidth.wide.name')}</h4>
              <p className="text-gray-600 text-sm">{t('floorInfo.oakWidth.wide.description')}</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <div className="text-4xl font-bold text-wood-brown mb-2">{t('floorInfo.oakWidth.extraWide.size')}</div>
              <h4 className="font-semibold mb-2">{t('floorInfo.oakWidth.extraWide.name')}</h4>
              <p className="text-gray-600 text-sm">{t('floorInfo.oakWidth.extraWide.description')}</p>
            </div>
          </div>
        </div>

        {/* LVT for Basements */}
        <div className="bg-blue-50 rounded-xl p-8 mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold text-wood-dark mb-6">
                {t('floorInfo.lvt.title')}
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong>{t('floorInfo.lvt.waterproof.title')}</strong> {t('floorInfo.lvt.waterproof.description')}
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong>{t('floorInfo.lvt.moldResistant.title')}</strong> {t('floorInfo.lvt.moldResistant.description')}
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong>{t('floorInfo.lvt.realistic.title')}</strong> {t('floorInfo.lvt.realistic.description')}
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong>{t('floorInfo.lvt.comfortable.title')}</strong> {t('floorInfo.lvt.comfortable.description')}
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1565538810643-b5bdb714032a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Modern basement with LVT flooring"
                className="rounded-lg mb-4"
              />
              <p className="text-center text-gray-600 italic">
                {t('floorInfo.lvt.caption')}
              </p>
            </div>
          </div>
        </div>

        {/* Benefits of 3/4" Hardwood */}
        <div className="bg-gradient-to-r from-wood-light/20 to-wood-brown/20 rounded-xl p-8">
          <h3 className="text-3xl font-bold text-wood-dark mb-8 text-center">
            {t('floorInfo.solidHardwood.title')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-wood-brown mb-4">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </div>
              <h4 className="text-xl font-semibold mb-2">{t('floorInfo.solidHardwood.squeaks.title')}</h4>
              <p className="text-gray-600">
                {t('floorInfo.solidHardwood.squeaks.description')}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-wood-brown mb-4">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
              <h4 className="text-xl font-semibold mb-2">{t('floorInfo.solidHardwood.quiet.title')}</h4>
              <p className="text-gray-600">
                {t('floorInfo.solidHardwood.quiet.description')}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-wood-brown mb-4">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h4 className="text-xl font-semibold mb-2">{t('floorInfo.solidHardwood.healthier.title')}</h4>
              <p className="text-gray-600">
                {t('floorInfo.solidHardwood.healthier.description')}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-wood-brown mb-4">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-xl font-semibold mb-2">{t('floorInfo.solidHardwood.durability.title')}</h4>
              <p className="text-gray-600">
                {t('floorInfo.solidHardwood.durability.description')}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-wood-brown mb-4">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-xl font-semibold mb-2">{t('floorInfo.solidHardwood.value.title')}</h4>
              <p className="text-gray-600">
                {t('floorInfo.solidHardwood.value.description')}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-wood-brown mb-4">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h4 className="text-xl font-semibold mb-2">{t('floorInfo.solidHardwood.insulation.title')}</h4>
              <p className="text-gray-600">
                {t('floorInfo.solidHardwood.insulation.description')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FloorInfo; 